import React, { useCallback, useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Calendar, Target, Pencil, Play, Trash2, Video } from 'lucide-react'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import PlanStatusBadge from '@/components/plans/PlanStatusBadge'
import EditPlanModal from '@/components/plans/EditPlanModal'
import GoalCard, { LinkedTaskSummary } from '@/components/plans/GoalCard'
import AddGoalForm, { GoalFormData } from '@/components/plans/AddGoalForm'
import LinkTaskToGoalModal from '@/components/plans/LinkTaskToGoalModal'
import ProgressBar from '@/components/plans/ProgressBar'
import { formatPlanDate } from '@/utils/date'
import { planService } from '@/services/planService'
import { taskService } from '@/services/taskService'
import { useToast } from '@/contexts/ToastContext'
import { useAuth } from '@/contexts/Auth0Context'
import { Plan, PlanGoal, PlanMeeting } from '@/types/plan'

const MEETING_TYPE_LABELS: Record<string, string> = {
  onboarding: 'Onboarding',
  regular_checkin: 'Regular Check-in',
  project_review: 'Project Review',
  career_guidance: 'Career Guidance',
  skill_assessment: 'Skill Assessment',
  goal_setting: 'Goal Setting',
  feedback_session: 'Feedback Session',
  other: 'Other'
}

const MEETING_STATUS_LABELS: Record<string, string> = {
  scheduled: 'Confirmed',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  rescheduled: 'Rescheduled'
}

const formatMeetingTime = (value: string): string =>
  new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })

const PlanDetailView: React.FC = () => {
  const { planId } = useParams<{ planId: string }>()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const { user } = useAuth()

  const [plan, setPlan] = useState<Plan | null>(null)
  const [loading, setLoading] = useState(true)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
  const [isMutating, setIsMutating] = useState(false)
  const [isGoalFormOpen, setIsGoalFormOpen] = useState(false)
  const [editingGoal, setEditingGoal] = useState<PlanGoal | null>(null)
  const [deletingGoal, setDeletingGoal] = useState<PlanGoal | null>(null)
  const [linkingGoal, setLinkingGoal] = useState<PlanGoal | null>(null)
  const [taskTitlesById, setTaskTitlesById] = useState<Record<string, string>>({})
  const [meetings, setMeetings] = useState<PlanMeeting[]>([])

  const fetchPlan = useCallback(async () => {
    if (!planId) return
    try {
      setLoading(true)
      const data = await planService.getPlan(planId)
      setPlan(data)
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to load plan'
      })
    } finally {
      setLoading(false)
    }
  }, [planId, showToast])

  useEffect(() => {
    fetchPlan()
  }, [fetchPlan])

  useEffect(() => {
    if (!planId) return
    planService
      .getMeetingsForPlan(planId)
      .then(setMeetings)
      .catch(() => undefined)
  }, [planId])

  // Resolve linked task titles for display
  useEffect(() => {
    if (!plan?.relationshipId) return
    const hasLinkedTasks = plan.goals.some((goal) => goal.linkedTaskIds.length > 0)
    if (!hasLinkedTasks) return

    taskService
      .getTasksForRelationship(plan.relationshipId)
      .then((tasks) => {
        const titles: Record<string, string> = {}
        for (const task of Array.isArray(tasks) ? tasks : []) {
          titles[task.id] = task.title
        }
        setTaskTitlesById(titles)
      })
      .catch(() => undefined)
  }, [plan])

  const linkedTasksFor = (goal: PlanGoal): LinkedTaskSummary[] =>
    goal.linkedTaskIds.map((taskId) => ({
      id: taskId,
      title: taskTitlesById[taskId] ?? 'Task'
    }))

  const isMentor = user?.role === 'mentor'
  const showDraftControls = isMentor && plan?.status === 'draft'
  const canManageGoals = isMentor && (plan?.status === 'draft' || plan?.status === 'active')

  const handleGoalSubmit = async (data: GoalFormData) => {
    if (!plan) return
    try {
      const updated = editingGoal
        ? await planService.updateGoal(plan.id, editingGoal.id, data)
        : await planService.addGoal(plan.id, data)
      setPlan(updated)
      setIsGoalFormOpen(false)
      setEditingGoal(null)
      showToast({ type: 'success', message: editingGoal ? 'Goal updated' : 'Goal added' })
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to save goal'
      })
    }
  }

  const handleUnlinkTask = async (goal: PlanGoal, taskId: string) => {
    if (!plan) return
    try {
      const updated = await planService.unlinkTask(plan.id, goal.id, taskId)
      setPlan(updated)
      showToast({ type: 'success', message: 'Task unlinked' })
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to unlink task'
      })
    }
  }

  const handleGoalDelete = async () => {
    if (!plan || !deletingGoal) return
    try {
      const updated = await planService.deleteGoal(plan.id, deletingGoal.id)
      setPlan(updated)
      showToast({ type: 'success', message: 'Goal deleted' })
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to delete goal'
      })
    } finally {
      setDeletingGoal(null)
    }
  }

  const handleActivate = async () => {
    if (!plan) return
    try {
      setIsMutating(true)
      const updated = await planService.activatePlan(plan.id)
      setPlan(updated)
      showToast({ type: 'success', message: 'Plan activated' })
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to activate plan'
      })
    } finally {
      setIsMutating(false)
    }
  }

  const handleDelete = async () => {
    if (!plan) return
    try {
      setIsMutating(true)
      await planService.deletePlan(plan.id)
      showToast({ type: 'success', message: 'Plan deleted' })
      navigate(`/plans/relationship/${plan.relationshipId}`)
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to delete plan'
      })
      setIsMutating(false)
      setIsDeleteConfirmOpen(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!plan) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-600">Plan not found.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <Link
          to={`/plans/relationship/${plan.relationshipId}`}
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to plans
        </Link>

        <Card className="mb-6">
          <CardBody>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-gray-900">{plan.title}</h1>
                  <PlanStatusBadge status={plan.status} />
                </div>
                {plan.description && (
                  <p className="mt-2 text-gray-600">{plan.description}</p>
                )}
                <div className="flex items-center gap-1 mt-3 text-sm text-gray-500">
                  <Calendar className="w-4 h-4" />
                  <span>
                    {formatPlanDate(plan.startDate)} – {formatPlanDate(plan.endDate)}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Progress</p>
                <p className="text-2xl font-bold text-gray-900">{plan.progressPercentage}%</p>
              </div>
            </div>

            <div className="mt-4">
              <ProgressBar value={plan.progressPercentage} label="Overall plan progress" />
            </div>

            {showDraftControls && (
              <div className="flex gap-2 mt-4 border-t pt-4">
                <Button variant="outline" size="sm" onClick={() => setIsEditOpen(true)} disabled={isMutating}>
                  <Pencil className="w-4 h-4 mr-1" />
                  Edit Plan
                </Button>
                <Button size="sm" onClick={handleActivate} disabled={isMutating}>
                  <Play className="w-4 h-4 mr-1" />
                  Activate Plan
                </Button>
                <Button variant="danger" size="sm" onClick={() => setIsDeleteConfirmOpen(true)} disabled={isMutating}>
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete Plan
                </Button>
              </div>
            )}
          </CardBody>
        </Card>

        {isEditOpen && plan && (
          <EditPlanModal
            plan={plan}
            onClose={() => setIsEditOpen(false)}
            onPlanUpdated={(updated) => setPlan(updated)}
          />
        )}

        {isDeleteConfirmOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Delete plan?</h2>
              <p className="text-sm text-gray-600 mb-4">
                This will permanently delete the plan and all of its goals. This cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setIsDeleteConfirmOpen(false)} disabled={isMutating}>
                  Cancel
                </Button>
                <Button variant="danger" size="sm" onClick={handleDelete} disabled={isMutating}>
                  Confirm
                </Button>
              </div>
            </div>
          </div>
        )}

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Goals</h2>
              {canManageGoals && !isGoalFormOpen && !editingGoal && (
                <Button
                  size="sm"
                  onClick={() => {
                    setEditingGoal(null)
                    setIsGoalFormOpen(true)
                  }}
                >
                  Add Goal
                </Button>
              )}
            </div>
          </CardHeader>
          <CardBody>
            {plan.goals.length === 0 && !isGoalFormOpen && (
              <div className="text-center py-6">
                <Target className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                <p className="text-gray-600">No goals yet.</p>
              </div>
            )}

            {plan.goals.length > 0 && (
              <div className="space-y-3">
                {plan.goals.map((goal) => (
                  <GoalCard
                    key={goal.id}
                    goal={goal}
                    isMentor={!!canManageGoals}
                    linkedTasks={linkedTasksFor(goal)}
                    onEdit={() => {
                      setEditingGoal(goal)
                      setIsGoalFormOpen(true)
                    }}
                    onDelete={() => setDeletingGoal(goal)}
                    onLinkTasks={() => setLinkingGoal(goal)}
                    onUnlinkTask={(taskId) => handleUnlinkTask(goal, taskId)}
                  />
                ))}
              </div>
            )}

            {isGoalFormOpen && (
              <div className="mt-4">
                <AddGoalForm
                  key={editingGoal?.id ?? 'new'}
                  initialGoal={editingGoal ?? undefined}
                  onSubmit={handleGoalSubmit}
                  onCancel={() => {
                    setIsGoalFormOpen(false)
                    setEditingGoal(null)
                  }}
                />
              </div>
            )}
          </CardBody>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <h2 className="text-lg font-semibold text-gray-900">Meetings</h2>
          </CardHeader>
          <CardBody>
            {meetings.length === 0 ? (
              <p className="text-sm text-gray-500">No meetings linked to this plan yet.</p>
            ) : (
              <div className="space-y-2">
                {meetings.map(meeting => (
                  <Link
                    key={meeting.id}
                    to={`/meetings/${meeting.id}`}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-primary-300 transition-colors"
                  >
                    <span className="flex items-center gap-2 text-sm text-gray-900">
                      <Video className="w-4 h-4 text-gray-500" />
                      {MEETING_TYPE_LABELS[meeting.type] || meeting.type}
                    </span>
                    <span className="flex items-center gap-3 text-sm text-gray-500">
                      {formatMeetingTime(meeting.scheduledStartTime)}
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        {MEETING_STATUS_LABELS[meeting.status] || meeting.status}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        {linkingGoal && plan && (
          <LinkTaskToGoalModal
            planId={plan.id}
            goalId={linkingGoal.id}
            relationshipId={plan.relationshipId}
            alreadyLinkedTaskIds={linkingGoal.linkedTaskIds}
            onClose={() => setLinkingGoal(null)}
            onLinked={(updated) => setPlan(updated)}
          />
        )}

        {deletingGoal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Delete goal?</h2>
              <p className="text-sm text-gray-600 mb-4">
                This will remove the goal "{deletingGoal.title}" from the plan.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setDeletingGoal(null)}>
                  Cancel
                </Button>
                <Button variant="danger" size="sm" onClick={handleGoalDelete}>
                  Confirm
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PlanDetailView
